import { batch } from 'react-redux';
import { setCustomParticipantVisible } from '../base/conference';
import { CONFERENCE_JOIN_IN_PROGRESS, CONFERENCE_LEFT } from '../base/conference/actionTypes';
import { getCurrentConference } from '../base/conference/functions';
import { SET_CONFIG } from '../base/config/actionTypes';
import { getPictureInPictureModeConfig } from '../base/config/functions.any';
import { MEDIA_TYPE } from '../base/media/constants';
import { FakeParticipant } from '../base/mtApi';
import { PARTICIPANT_LEFT } from '../base/participants/actionTypes';
import { participantJoined, participantLeft, pinParticipant } from '../base/participants/actions';
import { getLocalParticipant, getParticipantById, getParticipantDisplayName } from '../base/participants/functions';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { handleSharedVideoStart, onPresentationSharingStopped } from '../conference/filmStripViewManager';
import { SET_DYNAMIC_BRANDING_DATA } from '../dynamic-branding/actionTypes';
import { showNotification, showWarningNotification } from '../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';
import { setLabelsVisible } from '../toolbox/actions';
import { RESET_SHARED_VIDEO_STATUS, SET_SHARED_VIDEO_STATUS } from './actionTypes';
import { hideConfirmPlayingDialog, resetSharedVideoStatus, setAllowedUrlDomians, setSharedVideoStatus, showConfirmPlayingDialog } from './actions';
import { DEFAULT_ALLOWED_URL_DOMAINS, PLAYBACK_START, PLAYBACK_STATUSES, SHARED_VIDEO, VIDEO_PLAYER_PARTICIPANT_NAME, YOUTUBE_PLAYER_PARTICIPANT_NAME } from './constants';
import { isSharedVideoEnabled, isSharingStatus, isURLAllowedForSharedVideo, sendShareVideoCommand } from './functions';
import logger from './logger';
/**
 * Middleware that captures actions related to video sharing and updates
 * components not hooked into redux.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    var _a, _b, _c, _d, _e, _f;
    const { dispatch, getState } = store;
    if (!isSharedVideoEnabled(getState())) {
        return next(action);
    }
    switch (action.type) {
        case CONFERENCE_JOIN_IN_PROGRESS: {
            const { conference } = action;
            const localParticipantId = (_a = getLocalParticipant(getState())) === null || _a === void 0 ? void 0 : _a.id;
            conference.addCommandListener(SHARED_VIDEO, ({ value, attributes }, from) => {
                const state = getState();
                const sharedVideoStatus = attributes.state;
                const { ownerId } = state['features/shared-video'];
                if (ownerId && ownerId !== from) {
                    // This guard exists to stop a second participant hijacking playback while someone
                    // else is sharing, but it used to drop EVERY command from a non-matching id -
                    // including 'stop', and including the owner's own commands after they reconnected.
                    const ownerStillPresent = Boolean(getParticipantById(state, ownerId));
                    if (sharedVideoStatus !== 'stop' && ownerStillPresent) {
                        logger.warn(`User with id: ${from} sent shared video command: ${sharedVideoStatus} while we are playing.`);
                        return;
                    }
                    logger.warn(`Accepting shared video command '${sharedVideoStatus}' from ${from} despite`
                        + ` recorded ownerId ${ownerId} (ownerPresent=${ownerStillPresent}) - the recorded`
                        + ' owner is stale or this is a stop, which must always be honoured.');
                }
                // Our own update echoed back after we already stopped must not
                // recreate the video tile.
                if (from === localParticipantId
                    && sharedVideoStatus !== PLAYBACK_START
                    && isSharingStatus(sharedVideoStatus)
                    && !state['features/shared-video'].videoUrl) {
                    return;
                }
                if (isSharingStatus(sharedVideoStatus)) {
                    // confirmShowVideo is undefined the first time we receive
                    // when confirmShowVideo is false we ignore everything except stop that resets it
                    if (state['features/shared-video'].confirmShowVideo === false) {
                        return;
                    }
                    // Check if PiP is active - if so, auto-accept YouTube videos without confirmation
                    const isPiPActive = getPictureInPictureModeConfig(state);
                    const isYouTubeVideo = !value.match(/http/);
                    const shouldAutoAccept = isPiPActive && isYouTubeVideo;
                    if (isURLAllowedForSharedVideo(value, state['features/shared-video'].allowedUrlDomains, true)
                        || localParticipantId === from
                        || state['features/shared-video'].confirmShowVideo // if confirmed skip asking again
                        || shouldAutoAccept) { // auto-accept when in PiP and YouTube
                        handleSharingVideoStatus(store, value, Object.assign(Object.assign({}, attributes), { from }), conference);
                    }
                    else {
                        dispatch(showConfirmPlayingDialog(getParticipantDisplayName(state, from), () => {
                            handleSharingVideoStatus(store, value, Object.assign(Object.assign({}, attributes), { from }), conference);
                            return true; // on mobile this is used to close the dialog
                        }));
                    }
                    return;
                }
                if (sharedVideoStatus === 'stop') {
                    const videoParticipant = getParticipantById(state, value);
                    const sharerDisplayName = getParticipantDisplayName(state, from);
                    // Determine if it's a YouTube video or regular video
                    const youtubeId = value.match(/http/) ? false : value;
                    const notificationKey = youtubeId
                        ? 'notify.stoppedSharingYouTubeVideo'
                        : 'notify.stoppedSharingVideo';
                    // Show notification that user stopped sharing video
                    dispatch(showNotification({
                        titleKey: notificationKey,
                        titleArguments: {
                            name: sharerDisplayName
                        }
                    }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
                    if (state['features/shared-video'].confirmShowVideo === false) {
                        dispatch(showWarningNotification({
                            titleKey: 'dialog.shareVideoLinkStopped',
                            titleArguments: {
                                name: sharerDisplayName
                            }
                        }, NOTIFICATION_TIMEOUT_TYPE.LONG));
                    }
                    dispatch(hideConfirmPlayingDialog());
                    dispatch(participantLeft(value, conference, {
                        fakeParticipant: videoParticipant === null || videoParticipant === void 0 ? void 0 : videoParticipant.fakeParticipant
                    }));
                    if (localParticipantId !== from) {
                        dispatch(resetSharedVideoStatus());
                    }
                }
            });
            break;
        }
        case CONFERENCE_LEFT:
            dispatch(setAllowedUrlDomians(DEFAULT_ALLOWED_URL_DOMAINS));
            dispatch(resetSharedVideoStatus());
            break;
        case PARTICIPANT_LEFT: {
            const state = getState();
            const conference = getCurrentConference(state);
            const { ownerId: stateOwnerId, videoUrl: statevideoUrl } = state['features/shared-video'];
            // Safety check: ensure action.participant exists before accessing its id
            if (((_b = action.participant) === null || _b === void 0 ? void 0 : _b.id) === stateOwnerId) {
                batch(() => {
                    dispatch(resetSharedVideoStatus());
                    dispatch(participantLeft(statevideoUrl !== null && statevideoUrl !== void 0 ? statevideoUrl : '', conference));
                });
            }
            break;
        }
        case SET_CONFIG:
        case SET_DYNAMIC_BRANDING_DATA: {
            const result = next(action);
            const state = getState();
            const { sharedVideoAllowedURLDomains: allowedURLDomainsFromConfig = [] } = state['features/base/config'];
            const { sharedVideoAllowedURLDomains: allowedURLDomainsFromBranding = [] } = state['features/dynamic-branding'];
            dispatch(setAllowedUrlDomians([
                ...DEFAULT_ALLOWED_URL_DOMAINS,
                ...allowedURLDomainsFromBranding,
                ...allowedURLDomainsFromConfig
            ]));
            return result;
        }
        case SET_SHARED_VIDEO_STATUS: {
            const state = getState();
            const conference = getCurrentConference(state);
            const localParticipantId = (_c = getLocalParticipant(state)) === null || _c === void 0 ? void 0 : _c.id;
            const { videoUrl, status, ownerId, time, muted, volume } = action;
            const { ownerId: currentOwnerId, videoUrl: currentVideoUrl } = state['features/shared-video'];
            const operator = status === PLAYBACK_STATUSES.PLAYING ? 'is' : '';
            // Same guard as native: once the local owner stopped, a late player
            // event (YouTube fires 'paused' while being torn down) still claims
            // local ownership. Swallow it so it neither refills the state nor
            // re-broadcasts the share to everyone else.
            if (status !== PLAYBACK_START
                && ownerId
                && ownerId === localParticipantId
                && !currentOwnerId
                && !currentVideoUrl) {
                logger.debug('Ignoring stale shared video status update received after sharing stopped.');
                return;
            }
            logger.debug(`User with id: ${ownerId} ${operator} ${status} video sharing.`);
            if (typeof APP !== 'undefined') {
                APP.API.notifyAudioOrVideoSharingToggled(MEDIA_TYPE.VIDEO, status, ownerId);
            }
            // when setting status we need to send the command for that, but not do it for the start command
            // as we are sending the command in playSharedVideo and setting the start status once
            // we receive the response, this way we will start the video at the same time when remote participants
            // start it, on receiving the command
            if (status === 'start') {
                break;
            }
            if (localParticipantId === ownerId) {
                sendShareVideoCommand({
                    conference,
                    localParticipantId,
                    muted,
                    status,
                    time,
                    id: videoUrl,
                    volume
                });
            }
            break;
        }
        case RESET_SHARED_VIDEO_STATUS: {
            const state = getState();
            const localParticipantId = (_d = getLocalParticipant(state)) === null || _d === void 0 ? void 0 : _d.id;
            const { ownerId: stateOwnerId, videoUrl: statevideoUrl } = state['features/shared-video'];
            if (!stateOwnerId) {
                break;
            }
            logger.debug(`User with id: ${stateOwnerId} stop video sharing.`);
            if (typeof APP !== 'undefined') {
                APP.API.notifyAudioOrVideoSharingToggled(MEDIA_TYPE.VIDEO, 'stop', stateOwnerId);
            }
            // Broadcast when we are the recorded owner, or when that owner has left - in which case nobody
            // else is going to send the stop and the video would otherwise play on for everyone.
            if (localParticipantId === stateOwnerId || !getParticipantById(state, stateOwnerId)) {
                const conference = getCurrentConference(state);
                sendShareVideoCommand({
                    conference,
                    id: statevideoUrl !== null && statevideoUrl !== void 0 ? statevideoUrl : '',
                    localParticipantId,
                    muted: true,
                    status: 'stop',
                    time: 0,
                    volume: 0
                });
            }
            const participants = state['features/base/participants'] || [];
            const isScreenSharing = participants.some(p => p.isFakeParticipant === 'LocalScreenShare' || p.isFakeParticipant === 'RemoteScreenShare');
            const isIframeSharing = Boolean(((_e = state['features/genericiframe']) === null || _e === void 0 ? void 0 : _e.visible) || ((_f = state['features/etherpad']) === null || _f === void 0 ? void 0 : _f.editing));
            if (!isScreenSharing && !isIframeSharing) {
                onPresentationSharingStopped(dispatch);
            }
            break;
        }
    }
    return next(action);
});
/**
 * Handles the playing, pause and start statuses for the shared video.
 * Dispatches participantJoined event and, if necessary, pins it.
 * Sets the SharedVideoStatus if the event was triggered by the local user.
 *
 * @param {Store} store - The redux store.
 * @param {string} videoUrl - The id of the video to the shared.
 * @param {Object} attributes - The attributes received from the share video command.
 * @param {JitsiConference} conference - The current conference.
 * @returns {void}
 */
function handleSharingVideoStatus(store, videoUrl, { state, time, from, muted }, conference) {
    var _a, _b, _c;
    const { dispatch, getState } = store;
    const localParticipantId = (_a = getLocalParticipant(getState())) === null || _a === void 0 ? void 0 : _a.id;
    const oldStatus = (_c = (_b = getState()['features/shared-video']) === null || _b === void 0 ? void 0 : _b.status) !== null && _c !== void 0 ? _c : '';
    const oldVideoUrl = getState()['features/shared-video'].videoUrl;
    if (oldVideoUrl && oldVideoUrl !== videoUrl) {
        logger.warn(`User with id: ${from} sent videoUrl: ${videoUrl} while we are playing: ${oldVideoUrl}`);
        return;
    }
    // If the video was not started (no participant) we want to create the participant
    // this can be triggered by start, but also by paused or playing
    // commands (joining late) and getting the current state
    if (state === PLAYBACK_START || !isSharingStatus(oldStatus)) {
        const youtubeId = videoUrl.match(/http/) ? false : videoUrl;
        const avatarURL = youtubeId ? `https://img.youtube.com/vi/${youtubeId}/0.jpg` : '';
        const participantName = youtubeId ? YOUTUBE_PLAYER_PARTICIPANT_NAME : VIDEO_PLAYER_PARTICIPANT_NAME;
        const sharerDisplayName = getParticipantDisplayName(getState(), from);
        // Check if PiP is active - if so, don't change view or pin (to avoid disrupting PiP)
        handleSharedVideoStart(dispatch, getState);
        dispatch(participantJoined({
            conference,
            fakeParticipant: FakeParticipant.SharedVideo,
            id: videoUrl,
            avatarURL,
            isFakeParticipant: true,
            name: participantName,
            ownerId: from
        }));
        // Show notification about who shared the video
        const notificationKey = youtubeId ? 'notify.sharedYouTubeVideo' : 'notify.sharedVideo';
        dispatch(showNotification({
            titleKey: notificationKey,
            titleArguments: {
                name: sharerDisplayName
            }
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        const isPiPActive = getPictureInPictureModeConfig(getState());
        // Only pin and change view if not in PiP mode
        if (!isPiPActive) {
            dispatch(pinParticipant(videoUrl, true));
            dispatch(setCustomParticipantVisible(false));
            dispatch(setLabelsVisible(false));
        }
        if (localParticipantId === from) {
            dispatch(setSharedVideoStatus({
                videoUrl,
                status: state,
                time: Number(time),
                ownerId: localParticipantId
            }));
        }
    }
    if (localParticipantId !== from) {
        dispatch(setSharedVideoStatus({
            muted: muted === 'true',
            ownerId: from,
            status: state,
            time: Number(time),
            videoUrl
        }));
    }
}
